import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { CardView, Stage } from "@breeze/shared";
import { STAGE_LABELS } from "@breeze/shared";
import { Card } from "./Card.js";
import { STAGE_ACCENT } from "../theme.js";

type Props = {
  stage: Stage;
  cards: CardView[];
  onOpenCard?: (cardId: number) => void;
  currentUser?: string;
};

export function Column({ stage, cards, onOpenCard, currentUser }: Props): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: `col:${stage}` });
  const accent = STAGE_ACCENT[stage];

  return (
    <section className="flex w-[18rem] shrink-0 flex-col overflow-hidden rounded-xl border border-ink-700 bg-ink-800/60 backdrop-blur-sm">
      <div
        className="h-[3px] w-full"
        style={{ backgroundColor: accent }}
        aria-hidden
      />
      <div className="flex items-center justify-between border-b border-ink-700 px-3 py-2.5">
        <div className="flex items-baseline gap-2">
          <span
            className="text-[13px] font-semibold uppercase tracking-[0.14em]"
            style={{ color: accent }}
          >
            {STAGE_LABELS[stage]}
          </span>
          <span className="text-[13px] font-semibold tabular-nums text-ink-50">
            {cards.length}
          </span>
        </div>
        {(() => {
          const total = cards.reduce((s, c) => s + (c.totalCost ?? 0), 0);
          if (total <= 0) return null;
          return (
            <span
              className="rounded-sm border border-ink-500 bg-ink-900/60 px-1.5 py-0.5 text-[12px] tabular-nums text-ink-50"
              title="Sum of totals in this column"
            >
              £{total.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          );
        })()}
      </div>
      <div
        ref={setNodeRef}
        className={`scrollbar-slim flex flex-1 flex-col overflow-y-auto p-2 transition-colors ${
          isOver ? "bg-slack-aubergine/15" : ""
        }`}
      >
        <SortableContext
          items={cards.map((c) => c.cardId)}
          strategy={verticalListSortingStrategy}
        >
          {cards.length === 0 ? null : cards.map((c) => (
            <Card
              key={c.cardId}
              card={c}
              onOpen={onOpenCard}
              {...(currentUser !== undefined ? { currentUser } : {})}
            />
          ))}
        </SortableContext>
        {/* Flexible drop spacer — fills whatever vertical space is left under
            the cards so users can drop anywhere in the column, even when it
            contains only one or two items. Highlights dashed on drag-over. */}
        <div
          className={`mt-1 flex-1 min-h-[5rem] rounded-md border border-dashed transition-all ${
            isOver
              ? "border-slack-aubergineLight bg-slack-aubergine/10"
              : cards.length === 0
                ? "border-ink-600"
                : "border-transparent"
          }`}
          aria-hidden
        >
          {cards.length === 0 ? (
            <div className="flex h-full items-center justify-center text-[12px] text-ink-50">
              Drop here
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
